import type { PerformanceKpiId } from "./my-brand-drawer-performance-catalog"
import { MY_BRAND_DRAWER_FATIGUE_CATALOG } from "./my-brand-drawer-fatigue-catalog"

export interface FatigueSeriesPoint {
  period: "current" | "previous"
  metric: string
  bucketIndex: number
  date: string
  value: number | null
}

export interface FatigueChartRow {
  bucketIndex: number
  bucketLabel: string
  [dataKey: string]: number | string | null | undefined
}

const SQL_SUFFIX_TO_ID = Object.fromEntries(
  MY_BRAND_DRAWER_FATIGUE_CATALOG.map((e) => [e.sqlSuffix, e.id]),
) as Record<string, PerformanceKpiId>

function firstNonNullValue(points: FatigueSeriesPoint[]): number | null {
  for (const p of points) {
    if (p.value !== null && p.value !== undefined && Number.isFinite(p.value)) {
      return p.value
    }
  }
  return null
}

function indexValue(value: number | null, baseline: number | null): number | null {
  if (value === null || baseline === null || baseline === 0) return null
  return (value / baseline) * 100
}

export function buildFatigueChartRows(
  points: FatigueSeriesPoint[],
  selectedIds: PerformanceKpiId[],
  indexed: boolean,
): FatigueChartRow[] {
  const bucketIndices = [...new Set(points.map((p) => p.bucketIndex))].sort((a, b) => a - b)
  const rows: FatigueChartRow[] = bucketIndices.map((bucketIndex) => {
    const sample = points.find((p) => p.bucketIndex === bucketIndex)
    return {
      bucketIndex,
      bucketLabel: sample?.date ?? String(bucketIndex),
    }
  })

  for (const id of selectedIds) {
    const entry = MY_BRAND_DRAWER_FATIGUE_CATALOG.find((e) => e.id === id)
    if (!entry) continue

    for (const period of ["current", "previous"] as const) {
      const periodPoints = points
        .filter((p) => p.metric === entry.sqlSuffix && p.period === period)
        .sort((a, b) => a.bucketIndex - b.bucketIndex)

      const baseline = indexed ? firstNonNullValue(periodPoints) : null
      const dataKey = `${id}_${period === "current" ? "cur" : "pre"}`

      for (const row of rows) {
        const pt = periodPoints.find((p) => p.bucketIndex === row.bucketIndex)
        const raw = pt?.value ?? null
        row[dataKey] = indexed ? indexValue(raw, baseline) : raw
      }
    }
  }

  return rows
}

export function parseFatigueSeriesRows(
  rows: Array<Record<string, unknown>>,
): FatigueSeriesPoint[] {
  return rows.map((row) => ({
    period: row.period === "previous" || row.period === "prior" ? "previous" : "current",
    metric: String(row.metric ?? ""),
    bucketIndex: Number(row.bucketIndex ?? row.bucket_index ?? 0),
    date: String(row.date ?? row.bucket_start_date ?? ""),
    value:
      row.value === null || row.value === undefined
        ? null
        : Number.isFinite(Number(row.value))
          ? Number(row.value)
          : null,
  }))
}

export function fatigueMetricIdFromSqlSuffix(suffix: string): PerformanceKpiId | undefined {
  return SQL_SUFFIX_TO_ID[suffix]
}
